import React, { useState } from 'react';
import { Box, Typography, Button, useTheme, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableQuestionEditor from './SortableQuestionEditor';
import { QuestionType } from '@/types/questionTypes'; // Adjust path
import {
  UiQuestion,
  UiTemplate
} from '@/pages/TemplateEditPage/templateEditTypes';
import {
  getTypeIcon,
  getTypeLabel,
  questionTypeDetails
} from '@/pages/TemplateEditPage/templateEditUtils';

interface QuestionListProps {
  template: UiTemplate; // Pass the whole template to get overall question index
  filteredQuestions: UiQuestion[];
  filteredQuestionIds: (string | number)[];
  expandedQuestions: (string | number)[];
  questionSearchTerm: string;
  onDragEnd: (event: DragEndEvent) => void;
  onLocalQuestionChange: (
    id: string | number,
    updates: Partial<UiQuestion>
  ) => void;
  onPersistQuestion: (
    id: string | number,
    updates: Partial<UiQuestion>
  ) => void;
  onDuplicateQuestion: (id: string | number) => void;
  onDeleteQuestion: (id: string | number) => void;
  onToggleExpandQuestion: (id: string | number) => void;
  onAddQuestion: (type: QuestionType) => void; // Changed from onAddQuestionTypeSelected
  onSearchChange: (term: string) => void;
  onExpandAllQuestions: () => void;
  onCollapseAllQuestions: () => void;
}

const QuestionList: React.FC<QuestionListProps> = ({
  template,
  filteredQuestions,
  filteredQuestionIds,
  expandedQuestions,
  questionSearchTerm,
  onDragEnd,
  onLocalQuestionChange,
  onPersistQuestion,
  onDuplicateQuestion,
  onDeleteQuestion,
  onToggleExpandQuestion,
  onAddQuestion,
  onSearchChange,
  onExpandAllQuestions,
  onCollapseAllQuestions
}) => {
  const theme = useTheme();
  const [addQuestionAnchorEl, setAddQuestionAnchorEl] = useState<null | HTMLElement>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const handleAddQuestionClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddQuestionAnchorEl(event.currentTarget);
  };
  
  const handleAddQuestionClose = () => {
    setAddQuestionAnchorEl(null);
  };
  
  const handleQuestionTypeSelect = (type: QuestionType) => {
    onAddQuestion(type);
    handleAddQuestionClose();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      {/* QuestionListControls removed - using top-level header button instead */}
      <Box sx={{ 
        px: 4, 
        pt: 3, 
        pb: 4,
        minHeight: '500px',
        backgroundColor: theme.palette.background.default,
        width: '100%'
      }}>
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          <SortableContext
            items={filteredQuestionIds}
            strategy={verticalListSortingStrategy}
          >
          {filteredQuestions.length > 0 ? (
            <>
              {filteredQuestions.map((question) => (
                <Box key={question.id} id={String(question.id)}>
                  {' '}
                  {/* Ensure ID is string for DOM */}
                  <SortableQuestionEditor
                    question={question}
                    index={template.questions.findIndex(
                      // Use full template.questions for correct index
                      (q) => q.id === question.id
                    )}
                    onLocalQuestionChange={onLocalQuestionChange}
                    onPersistQuestion={onPersistQuestion}
                    onDuplicate={onDuplicateQuestion}
                    onDelete={onDeleteQuestion}
                    onAddQuestion={onAddQuestion}
                    isExpanded={expandedQuestions.includes(question.id)}
                    toggleExpand={() => onToggleExpandQuestion(question.id)}
                  />
                </Box>
              ))}
              
              {/* Add Another Question Card - only show when not searching */}
              {!questionSearchTerm && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: '8px',
                    backgroundColor: theme.palette.background.paper,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#9ca3af',
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                  onClick={handleAddQuestionClick}
                >
                  <AddIcon sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500
                    }}
                  >
                    Add Another Question
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.5
                    }}
                  >
                    Click to add a new question to this template
                  </Typography>
                </Box>
              )}
            </>
          ) : (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              mt: 2,
              background: theme.palette.background.default
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {questionSearchTerm
                ? 'No questions match your search.'
                : 'No questions have been added to this template yet.'}
            </Typography>
            {!questionSearchTerm && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  onAddQuestion(QuestionType.ShortText);
                }}
                sx={{ mt: 2 }}
              >
                Add First Question
              </Button>
            )}
          </Box>
        )}
        </SortableContext>
        </Box>
      </Box>
      
      {/* Question Type Selection Menu - Same as header Add Question */}
      <Menu
        anchorEl={addQuestionAnchorEl}
        open={Boolean(addQuestionAnchorEl)}
        onClose={handleAddQuestionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        PaperProps={{ 
          style: { maxHeight: 400, width: '250px' } 
        }}
        MenuListProps={{ dense: true }}
      >
        {questionTypeDetails.map((qt) => (
          <MenuItem
            key={qt.type}
            onClick={() => handleQuestionTypeSelect(qt.type)}
          >
            <ListItemIcon sx={{ minWidth: '36px' }}>
              {qt.icon}
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { variant: 'body2' } }}>
              {qt.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </DndContext>
  );
};

export default QuestionList;
